import { environment } from "../../config/environment";
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { customFetch } from "../../utils/Auth";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import DoctorSearch from "./DoctorSearch";
import SupportAgentChat from "./SupportAgentChat";
import TicketBuckets from "./TicketBuckets";
import {
  supportDoctorPath,
  supportMrnPath,
  supportPatientPath,
  supportTicketPath,
} from "./supportUrls";

const LOOKUP_METHODS = [
  {
    key: "ticket",
    label: "Pylon Ticket",
    description:
      "Enter a Pylon ticket number to review the conversation and get an AI recommendation.",
  },
  {
    key: "patient-id",
    label: "Patient ID",
    description: "Enter the patient ID if you already have it.",
  },
  {
    key: "mrn",
    label: "MRN",
    description:
      "Enter the medical record number. If several patients match, pick the correct one.",
  },
  {
    key: "doctor",
    label: "Doctor",
    description: "Find a doctor and browse their patients by day.",
  },
];

const DEFAULT_METHOD = "ticket";

const SupportHome = () => {
  const navigate = useNavigate();
  // The selected lookup method lives in the URL (?method=…) so it survives
  // reloads and back-navigation, and specific modes can be linked directly.
  const [searchParams, setSearchParams] = useSearchParams();
  const methodParam = searchParams.get("method");
  const lookupMethod = LOOKUP_METHODS.some((m) => m.key === methodParam)
    ? methodParam
    : DEFAULT_METHOD;
  const [patientIdInput, setPatientIdInput] = useState("");
  const [mrnInput, setMrnInput] = useState("");
  const [ticketInput, setTicketInput] = useState("");
  const [lookupError, setLookupError] = useState(null);
  const [allDoctors, setAllDoctors] = useState([]);
  const [isLoadingDoctors, setIsLoadingDoctors] = useState(true);
  const [doctorsLoadError, setDoctorsLoadError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setIsLoadingDoctors(true);
      setDoctorsLoadError(null);

      try {
        const response = await customFetch(
          `${environment.apiUrl}/doctors/all-doctors`,
          { method: "GET" }
        );
        const data = await response.json();

        if (cancelled) return;

        if (!response.ok) {
          setDoctorsLoadError(
            typeof data === "string" ? data : "Failed to load doctors"
          );
          setAllDoctors([]);
          return;
        }

        setAllDoctors(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) {
          setDoctorsLoadError("Failed to load doctors");
          setAllDoctors([]);
        }
      } finally {
        if (!cancelled) {
          setIsLoadingDoctors(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleMethodChange = (key) => {
    setSearchParams(key === DEFAULT_METHOD ? {} : { method: key }, {
      replace: false,
    });
    setLookupError(null);
    setPatientIdInput("");
    setMrnInput("");
    setTicketInput("");
  };

  const handlePatientIdSearch = (e) => {
    e.preventDefault();
    const trimmed = patientIdInput.trim();
    if (!trimmed) {
      setLookupError("Please enter a patient ID.");
      return;
    }
    if (!/^\d+$/.test(trimmed)) {
      setLookupError("Patient ID must be a number.");
      return;
    }
    setLookupError(null);
    navigate(supportPatientPath(trimmed));
  };

  const handleMrnSearch = (e) => {
    e.preventDefault();
    const trimmed = mrnInput.trim();
    if (!trimmed) {
      setLookupError("Please enter an MRN.");
      return;
    }
    setLookupError(null);
    navigate(supportMrnPath(trimmed));
  };

  const handleTicketSearch = (e) => {
    e.preventDefault();
    const trimmed = ticketInput.trim();
    if (!trimmed) {
      setLookupError("Please enter a ticket number.");
      return;
    }
    setLookupError(null);
    navigate(supportTicketPath(trimmed));
  };

  return (
    <div className="flex flex-col w-full max-w-5xl mx-auto">
      <h1 className="font-bold text-[28px] text-ktx-purple mt-8">Customer Support</h1>

      <section className="mt-6 rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-800">Find a patient or ticket</h2>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {LOOKUP_METHODS.map((method) => (
            <button
              key={method.key}
              type="button"
              onClick={() => handleMethodChange(method.key)}
              className={`text-left p-3 rounded-lg border-2 transition-colors ${
                lookupMethod === method.key
                  ? "border-ktx-purple bg-purple-50"
                  : "border-gray-200 hover:border-gray-300 bg-white"
              }`}
            >
              <p className="font-semibold text-gray-900 text-sm">{method.label}</p>
              <p className="text-xs text-gray-600 mt-1">{method.description}</p>
            </button>
          ))}
        </div>

        <div className="mt-5 pt-5 border-t border-gray-200">
          {lookupMethod === "patient-id" && (
            <form onSubmit={handlePatientIdSearch} className="space-y-3 max-w-md">
              <div>
                <label
                  htmlFor="patientId"
                  className="block text-sm font-medium text-gray-700 mb-1.5"
                >
                  Patient ID
                </label>
                <Input
                  id="patientId"
                  type="text"
                  value={patientIdInput}
                  onChange={(e) => setPatientIdInput(e.target.value)}
                  placeholder="e.g. 12345678"
                />
              </div>
              <Button type="submit" className="bg-ktx-purple hover:bg-purple-700">
                Find patient
              </Button>
            </form>
          )}

          {lookupMethod === "mrn" && (
            <form onSubmit={handleMrnSearch} className="space-y-3 max-w-md">
              <div>
                <label htmlFor="mrn" className="block text-sm font-medium text-gray-700 mb-1.5">
                  MRN (Medical Record Number)
                </label>
                <Input
                  id="mrn"
                  type="text"
                  value={mrnInput}
                  onChange={(e) => setMrnInput(e.target.value)}
                  placeholder="Enter MRN..."
                />
              </div>
              <Button type="submit" className="bg-ktx-purple hover:bg-purple-700">
                Search by MRN
              </Button>
            </form>
          )}

          {lookupMethod === "doctor" && (
            <DoctorSearch
              allDoctors={allDoctors}
              isLoadingDoctors={isLoadingDoctors}
              doctorsLoadError={doctorsLoadError}
              onDoctorSelected={(doctor) => {
                navigate(supportDoctorPath(doctor.user_id), { state: { doctor } });
              }}
            />
          )}

          {lookupMethod === "ticket" && (
            <div className="space-y-5">
              <form onSubmit={handleTicketSearch} className="space-y-3 max-w-md">
                <div>
                  <label
                    htmlFor="ticketId"
                    className="block text-sm font-medium text-gray-700 mb-1.5"
                  >
                    Ticket number
                  </label>
                  <Input
                    id="ticketId"
                    type="text"
                    value={ticketInput}
                    onChange={(e) => setTicketInput(e.target.value)}
                    placeholder="e.g. 1234"
                  />
                </div>
                <Button type="submit" className="bg-ktx-purple hover:bg-purple-700">
                  Find ticket
                </Button>
              </form>

              <TicketBuckets />

              <div className="flex items-center gap-3">
                <span className="h-px flex-1 bg-gray-200" />
                <span className="text-xs font-medium uppercase tracking-wide text-gray-400">
                  or ask the agent directly
                </span>
                <span className="h-px flex-1 bg-gray-200" />
              </div>

              <SupportAgentChat />
            </div>
          )}
        </div>

        {lookupError && (
          <p className="mt-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
            {lookupError}
          </p>
        )}
      </section>
    </div>
  );
};

export default SupportHome;
